/**
 * @file carrier-settings-form.ts
 * @description Преобразования формы настроек перевозчика (phase19.13).
 *
 * Вынесено из `ShopCarrierSettingsPage`: страница отвечает за экран, а
 * «DTO → поля» и «поля → тело PUT» — чистые функции, которые можно проверить
 * без рендера. Валидация здесь же: правило «сколько минут допустимо» не должно
 * жить в разметке.
 */

import type { DeliveryFormState } from "../components/carriers/CarrierDeliveryFields";
import type { MainFormState } from "../components/carriers/CarrierMainFields";
import {
  normalizePickupSyncScope,
  type ShopCarrierConfigDto,
  type UpsertShopCarrierConfigInput,
} from "./api-shop-carriers";

/** Полное состояние формы страницы настроек — обе секции полей. */
export type CarrierFormState = MainFormState & DeliveryFormState;

/** number | null → строка поля (пустая = «не задано»). */
export function numToInput(v: number | null | undefined): string {
  return v === null || v === undefined ? "" : String(v);
}

/** Пустое поле → null. Валидность значений проверяет сервер. */
export function inputToNum(v: string): number | null {
  const trimmed = v.trim();
  if (!trimmed) return null;
  const n = Number(trimmed.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

/** Конфиг с сервера → состояние формы. */
export function carrierConfigToForm(c: ShopCarrierConfigDto): CarrierFormState {
  return {
    isActive: c.isActive,
    priority: String(c.priority),
    cashOnDelivery: c.cashOnDelivery,
    onlinePayment: c.onlinePayment,
    cityWhitelist: c.cityWhitelist.join(", "),
    maxWeightG: c.maxWeightG ? String(c.maxWeightG) : "",
    maxDimensions: c.maxDimensions ?? "",
    pickupSyncScope: normalizePickupSyncScope(c.pickupSyncScope),
    pickupSyncIntervalHours: c.pickupSyncIntervalHours ?? null,
    deliverySyncIntervalMinutes: String(c.deliverySyncIntervalMinutes ?? 60),
    checkoutMapProvider: c.checkoutMapProvider ?? "AUTO",
    senderCityCode: numToInput(c.senderCityCode),
    senderPostalCode: c.senderPostalCode ?? "",
    senderAddress: c.senderAddress ?? "",
    senderPickupPointCode: c.senderPickupPointCode ?? "",
    handoverMode: c.handoverMode ?? "DROP_AT_PICKUP_POINT",
    defaultBoxLengthMm: numToInput(c.defaultBoxLengthMm),
    defaultBoxWidthMm: numToInput(c.defaultBoxWidthMm),
    defaultBoxHeightMm: numToInput(c.defaultBoxHeightMm),
    fallbackWeightG: numToInput(c.fallbackWeightG),
    publicTariffCodes: c.publicTariffCodes ?? [],
    markupPercent: numToInput(c.markupPercent),
    markupAmount: numToInput(c.markupAmount),
    freeShippingThreshold: numToInput(c.freeShippingThreshold),
    yandexMapsApiKey: c.yandexMapsApiKey ?? "",
  };
}

/**
 * Состояние формы → тело PUT либо текст ошибки для тоста.
 *
 * Проверяем только то, что сервер не сможет отличить от «не задано»: приоритет
 * и период автоопроса (0 = выключен, поэтому пустое поле трактуем как 0, иначе
 * автоопрос молча остался бы на старом значении).
 */
export function carrierFormToInput(
  form: CarrierFormState,
): { input: UpsertShopCarrierConfigInput } | { error: string } {
  const priority = Number(form.priority);
  if (Number.isNaN(priority) || priority < 0) {
    return { error: "Приоритет должен быть числом ≥ 0" };
  }

  const syncMinutes = Number(form.deliverySyncIntervalMinutes.trim() || "0");
  if (!Number.isInteger(syncMinutes) || syncMinutes < 0 || syncMinutes > 1440) {
    return { error: "Автоопрос статусов — целое число минут от 0 до 1440" };
  }

  return {
    input: {
      isActive: form.isActive,
      priority,
      cashOnDelivery: form.cashOnDelivery,
      onlinePayment: form.onlinePayment,
      cityWhitelist: form.cityWhitelist
        .split(/[,;\n]/u)
        .map((s) => s.trim())
        .filter(Boolean),
      maxWeightG: inputToNum(form.maxWeightG),
      maxDimensions: form.maxDimensions.trim() || null,
      pickupSyncScope: form.pickupSyncScope,
      pickupSyncIntervalHours: form.pickupSyncIntervalHours,
      deliverySyncIntervalMinutes: syncMinutes,
      checkoutMapProvider: form.checkoutMapProvider,
      senderCityCode: inputToNum(form.senderCityCode),
      senderPostalCode: form.senderPostalCode.trim() || null,
      senderAddress: form.senderAddress.trim() || null,
      senderPickupPointCode: form.senderPickupPointCode.trim() || null,
      handoverMode: form.handoverMode,
      defaultBoxLengthMm: inputToNum(form.defaultBoxLengthMm),
      defaultBoxWidthMm: inputToNum(form.defaultBoxWidthMm),
      defaultBoxHeightMm: inputToNum(form.defaultBoxHeightMm),
      fallbackWeightG: inputToNum(form.fallbackWeightG),
      publicTariffCodes: form.publicTariffCodes,
      markupPercent: inputToNum(form.markupPercent) ?? 0,
      markupAmount: inputToNum(form.markupAmount) ?? 0,
      freeShippingThreshold: inputToNum(form.freeShippingThreshold),
      yandexMapsApiKey: form.yandexMapsApiKey.trim() || null,
    },
  };
}
